import * as Sentry from '@sentry/react';

import type { TAppVersion } from '@/shared/hooks/use-app-version-refresh-prompt/use-app-version-refresh-prompt.types';

// A missing or non-JSON version file degrades update prompts silently forever,
// so report it once per session — the check itself runs every few minutes.
let hasReportedVersionFileProblem = false;

export const reportVersionFileProblem = (
  versionUrl: string,
  reason: string,
) => {
  if (hasReportedVersionFileProblem) {
    return;
  }

  hasReportedVersionFileProblem = true;

  console.warn('App version file unavailable', { versionUrl, reason });
  Sentry.captureMessage('App version file unavailable', {
    level: 'warning',
    extra: { versionUrl, reason },
  });
};

// Exported for tests only: per-session dedupe state would otherwise leak
// across describe/it boundaries and silently mask assertions.
export const __resetVersionFileProblemReportForTests = () => {
  hasReportedVersionFileProblem = false;
};

export const isAppVersion = (value: unknown): value is TAppVersion =>
  Boolean(value && typeof value === 'object' && 'version' in value);

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return '';
};

export const isChunkLoadError = (error: unknown) =>
  /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|CSS_CHUNK_LOAD_FAILED/i.test(
    getErrorMessage(error),
  );
